//#region imports
import React, { useEffect, useState } from 'react';

//bootstrap
import { Alert } from 'react-bootstrap';

// custom components
import { CustomSpinner, Modal } from '_atoms';
import RetailerTitle from './components/retailer-title';
import ProductList from './components/product-list';
import RetailerConfirmationModal from './components/retailer-confirmation-modal';
import RetailerOfflineModal from './components/retailer-offline-modal';
import MarketplaceFooter from './components/marketplace-footer';
import CheckoutButton from './components/checkout-button';

// nav
import { useHistory } from 'react-router-dom';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { getUserMarketplaceProducts, selectUserMarketplaceProducts, selectUserMarketplaceProductsStatus, selectUserMarketplaceProductsError } from '_redux/slices/marketplace/user-marketplace-products';
import { getRetailer, selectOneRetailer, selectGettingRetailerStatus, selectOneRetailerError } from '_redux/slices/marketplace/retailer';
import { setTableNumber, selectTableNumber, selectRetailerId, setRetailerId, selectManuallySet, selectShoppingCart } from '_redux/slices/marketplace/user-marketplace';

// styling
import { Spacing } from '_styles';


//#endregion imports

const RetailerProductScreen = (props) => {

    // nav
    const history = useHistory();

    //#region local states
    const [loading, setLoading] = useState(true);                                   // on initalizing we are loading
    const [error, setError] = useState(null);                                       // error state for products and retailer
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);      // show table confirmation modal
    const [showScrollTop, setShowScrollTop] = useState(false);                      // show scroll to top button
    const [cartQuantity, setCartQuantity] = useState(0);                            // get quantity from cart

    // retailer status
    const [retailerOnline, setRetailerOnline] = useState(false);                    // true if retailer is online, false if offline
    const [showOfflineModal, setShowOfflineModal] = useState(false);                // show retailer offline modal
    //#endregion local states

    //#region redux
    const dispatch = useDispatch();

    // retailer info
    const retailer = useSelector(selectOneRetailer);                                // retailer object
    const retailerStatus = useSelector(selectGettingRetailerStatus);                // status of getting retailer info     
    const retailerId = useSelector(selectRetailerId);                               // retailer id from url           
    const retailerError = useSelector(selectOneRetailerError);                      // error message if retailer info failed to load 

    // product list
    const products = useSelector(selectUserMarketplaceProducts);                    // products list from redux 
    const productsStatus = useSelector(selectUserMarketplaceProductsStatus);        // status of getting products
    const productsError = useSelector(selectUserMarketplaceProductsError);          // error message if products failed to load

    // table number
    const tableNumber = useSelector(selectTableNumber);                             // selected table number
    const manuallySet = useSelector(selectManuallySet);                             // check if table number was manually set in modal

    // shopping cart
    const cart = useSelector(selectShoppingCart);                                   // get shopping cart
    //#endregion redux


    //#region use effects

    // scroll listener to show/hide the back to top button
    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 300); // show after 300px
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // extracts retailer id from url and fetches retailer info and products
    useEffect(() => {
        const path = window.location.pathname;
        const query = new URLSearchParams(window.location.search);

        // Extract retailerId from the URL path
        const pathSegments = path.split('/');
        const tempRetailerId = pathSegments[pathSegments.length - 1];

        // Extract seat number (tableNumber) from the query string
        const tempTableNumber = query.get('tableNumber');

        // set retailer id and get retailer info and products
        if (tempRetailerId) {
            dispatch(setRetailerId(tempRetailerId));
            dispatch(getRetailer(tempRetailerId));
            dispatch(getUserMarketplaceProducts(tempRetailerId));
        }

        if (tempTableNumber) {
            // You can dispatch or setState here if needed
            dispatch(setTableNumber(tempTableNumber));
        }
    }, []);

    // updates table number in url
    useEffect(() => {
        if (tableNumber && manuallySet) {
            const currentPath = new URLSearchParams(window.location.search);
            const currentTable = currentPath.get('tableNumber');

            if (currentTable !== String(tableNumber)) {
                currentPath.set('tableNumber', tableNumber);
                history.replace(`${window.location.pathname}?${currentPath.toString()}`);
            }
        }
    }, [tableNumber, manuallySet, history]);

    // show confirmation modal if retailerId and tableNumber are set
    useEffect(() => {
        if (retailerId && tableNumber) {
            // show confirmation modal
            setShowConfirmationModal(true);
        }
    }, [tableNumber, retailerId]);

    // status listener for products and retailer
    useEffect(() => {
        if (retailerStatus === 'succeeded' && productsStatus === 'succeeded') {
            setLoading(false);
            setError(null);
        }
        // need to handle error listener
        // make sure correct error message shown here if failed
        if (productsStatus === 'failed') {
            setLoading(false);
            setError(productsError || 'Failed to load products. Please try again later.');
        }

        if (retailerStatus === 'failed') {
            setLoading(false);
            setError(retailerError || 'Failed to load retailer information. Please try again later.');
        }

        if (retailerStatus === 'loading' || productsStatus === 'loading') {
            setLoading(true);
            setError(null);
        }

    }, [retailerStatus, productsStatus]);

    // retailer status listener
    useEffect(() => {
        if (retailer && retailer?.status === 'online') {
            setRetailerOnline(true);
        }
        else if (retailer == null) {
            // if retailer is null, wait for retailer to be fetched or leave it to show error message
            console.log('do nothing...')
            setRetailerOnline(true);
        }
        else {
            setRetailerOnline(false);
        }
    }, [retailer]);

    // show offline modal if retailer is offline
    useEffect(() => {
        if (!retailerOnline) {
            setShowOfflineModal(true);
            setShowConfirmationModal(false);
        } else {
            setShowOfflineModal(false);
        }
    }, [retailerOnline]);

    // calculate total quantity of items in cart
    useEffect(() => {
        const quantity = Object.values(cart).reduce((sum, item) => sum + (item?.quantity || 0), 0);
        setCartQuantity(quantity);
    }, [cart]);

    //#endregion


    //#region functions

    // scroll to top of page
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // handle clicking logo in retailer title (banner)
    const handleLogoClick = () => {
        // trigger refresh
        window.location.reload()
    }
    //#endregion

    return (
        <div style={styles.pageContainer}>
            <RetailerTitle retailer={retailer} handleLogoClick={handleLogoClick} />

            {loading == true ? <CustomSpinner rowCenter={true} style={{ marginTop: Spacing.SCALE_22 }} /> : error ?
                <div style={{ padding: Spacing.SCALE_12 }}>
                    <Alert variant="danger" style={{ margin: '20px auto' }}>{'Issue loading retailer products, please refresh the page'}</Alert>
                </div> : (
                    <>
                        <ProductList products={products} />

                        {Object.values(cart).length > 0 && (
                            <div style={styles.stickyWrapper}>
                                <CheckoutButton
                                    loading={loading}
                                    error={error}
                                    cartQuantity={cartQuantity}
                                    showScrollTop={showScrollTop}
                                    scrollToTop={scrollToTop}
                                />
                            </div>
                        )}

                        <MarketplaceFooter />
                    </>

                )}


            {/* table / retailer confirmation modal */}
            {showConfirmationModal &&
                <Modal>
                    <RetailerConfirmationModal done={() => { setShowConfirmationModal(false) }} />
                </Modal>
            }

            {/* retailer offline modal */}
            {showOfflineModal &&
                <Modal>
                    <RetailerOfflineModal done={() => { setShowOfflineModal(false) }} />
                </Modal>
            }

        </div>
    );
};


//#region styles
const styles = {
    pageContainer: {
        position: 'relative',
        minHeight: '100vh'
    },
    stickyWrapper: {
        position: 'sticky',
        bottom: 0,
        zIndex: Spacing.SCALE_100,
        paddingTop: Spacing.SCALE_2,
        paddingBottom: Spacing.SCALE_12,
    },

};
//#endregion


export default RetailerProductScreen;
